import { Checkbox } from 'components/Inputs/Checkbox';
import { MaskedInput } from 'components/Inputs/MaskedInput';
import { Switch } from 'components/Inputs/Switch';
import { TextInput } from 'components/Inputs/TextInput';
import { useUser } from 'hooks/useUser';
import React from 'react';

import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';
import { Grid, InputAdornment, Typography } from '@mui/material';
import { Box } from '@mui/system';

import { PartnerAddressForm } from './AddressForm';
import { ContractForm } from './ContractForm';
import DiscountsForm from './Discounts/DiscountsForm';
import OpeningHoursForm from './OpeningHours/OpeningHoursForm';
import PartnerImageForm from './PartnerImageForm';
import { PartnershipTerms } from './PartnershipTerms';
import { PartnerTypeForm } from './PartnerTypeForm';
import { IPartnerCreate } from 'types/Partner';

type Props = {
  update?: boolean;
};

export const PartnerForm: React.FC<Props> = ({ update }) => {
  const { isAdmin, isPartner, user } = useUser();
  const partner = user as IPartnerCreate;
  /* If user is partner and has completed registration */
  const notFirstAccess =
    isPartner && partner.acceptPartnershipTerms?.isAccepted;
  const requiredFields = isPartner;

  return (
    <>
      <Box p={2}>
        <Grid container alignItems="flex-start" spacing={3}>
          <Grid item container spacing={2}>
            <Grid item container>
              <Grid item container xs={12} md={10}>
                <Grid
                  justifyContent="flex-start"
                  alignItems="center"
                  item
                  lg={4}
                >
                  <Typography
                    variant="button"
                    color="inherit"
                    noWrap
                    sx={{ fontSize: 20, fontWeight: 500 }}
                  >
                    DADOS CADASTRAIS
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <PartnerTypeForm />
                </Grid>
              </Grid>

              <Grid item container xs={12} md={2} justifyContent="flex-end">
                <PartnerImageForm />
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextInput
                name="socialReason"
                id="socialReason"
                label="RAZÃO SOCIAL"
                required={requiredFields}
                disabled={notFirstAccess}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextInput
                required
                name="company"
                id="company"
                label="NOME FANTASIA"
                disabled={notFirstAccess}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <MaskedInput
                name="cnpj"
                id="cnpj"
                label="CNPJ"
                placeholder="__.___.___/____-__"
                mask="##.###.###/####-##"
                required={requiredFields}
                disabled={notFirstAccess}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <MaskedInput
                name="cpf"
                id="cpf"
                label="CPF DO RESPONSÁVEL"
                placeholder="___.___.___-__"
                mask="###.###.###-##"
                required={requiredFields}
                disabled={notFirstAccess}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextInput
                name="responsible"
                id="responsible"
                label="NOME COMPLETO DO RESPONSÁVEL"
                required={requiredFields}
                disabled={notFirstAccess}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <MaskedInput
                name="phone"
                id="phone"
                label="TELEFONE FIXO"
                mask="(##) ####-####"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <MaskedInput
                name="mobilePhone.number"
                id="mobilePhone.number"
                label="TELEFONE CELULAR"
                mask="(##) #####-####"
                required={requiredFields}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Checkbox
                        name="mobilePhone.isWhatsapp"
                        id="mobilePhone.isWhatsapp"
                        label="É WhatsApp?"
                        labelPlacement="end"
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <PartnerAddressForm />

            <Grid item xs={12} md={4}>
              <OpeningHoursForm />
            </Grid>

            <Grid item xs={12} md={4}>
              <DiscountsForm />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextInput required name="email" id="email" label="EMAIL" />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextInput
                name="socialMedias.website"
                id="website"
                label="SITE"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <LanguageIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextInput
                name="socialMedias.instagram"
                id="instagram"
                label="INSTAGRAM"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <InstagramIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextInput
                name="socialMedias.facebook"
                id="facebook"
                label="FACEBOOK"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <FacebookIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {isPartner && <PartnershipTerms />}

            <Grid item container>
              <ContractForm />

              {update && isAdmin && (
                <Grid
                  item
                  md={2}
                  xs={12}
                  display="flex"
                  justifyContent="flex-end"
                  alignContent="flex-end"
                  alignItems="flex-end"
                >
                  <Switch
                    name="active"
                    activeLabel="Parceiro Ativo"
                    inactiveLabel="Parceiro Inativo"
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
